/**
 * The online-ordering menu, built from raw Firestore documents.
 *
 * Two sources feed the ordering menu the SAME documents:
 *
 *   1. the browser, reading Firestore through the client services
 *      (`menuGroupService`, `itemService`, `modifierGroupService`), and
 *   2. a server-rendered page (the restaurant site's `/menu`), which already
 *      read those collections with the admin SDK and hands them to
 *      `MenuPageClient` as `initialMenu` so the menu paints with NO browser
 *      Firestore read — no reCAPTCHA, no App Check token, no anonymous sign-in
 *      in front of first paint (measured 4.7 s on House of Pho; and a browser
 *      App Check refuses never saw a menu at all).
 *
 * Both run through the functions below — the services call them on what the
 * SDK returned, the page calls them on what the server returned. A second copy
 * of any of this (the modifier join in particular) is how a seeded menu and a
 * fetched menu would come to disagree, each looking correct on its own.
 *
 * DATA ONLY. Nothing here prices anything: pricing stays in the resolvers
 * (`resolveItemPrice` & co.) that read these models, per
 * docs/MENU_PRICING_ARCHITECTURE.md.
 */
import { MenuGroup, Item, Modifier, ModifierGroup } from '@order-up/models';
import {
  type DocumentWithId,
  modelsFromDocs,
  type RawFirestoreDoc,
} from '../interfaces/modelsFromDocs';

/**
 * The menu documents a server read, in wire form. Timestamps arrive serialized
 * (ISO strings) — every model's `fromJSON` accepts that as well as a live
 * Timestamp.
 */
export interface InitialMenuDocs {
  /** Epoch ms at which the server read these documents (React Query `updatedAt`). */
  loadedAt: number;
  menuGroups: RawFirestoreDoc[];
  items: RawFirestoreDoc[];
  modifierGroups: RawFirestoreDoc[];
  /** Needed because a modifier group stores only REFERENCES to its modifiers. */
  modifiers: RawFirestoreDoc[];
}

/** Where a set of documents came from, for the skipped-document log. */
export interface DocsSource {
  context: string;
  collectionPath: string;
}

const seedSource = (collection: string): DocsSource => ({
  context: 'server seed',
  collectionPath: collection,
});

export function menuGroupsFromDocs(
  docs: readonly RawFirestoreDoc[],
  source: DocsSource = seedSource('menu_groups'),
): DocumentWithId<MenuGroup>[] {
  return modelsFromDocs(MenuGroup, docs, source);
}

export function itemsFromDocs(
  docs: readonly RawFirestoreDoc[],
  source: DocsSource = seedSource('items'),
): DocumentWithId<Item>[] {
  return modelsFromDocs(Item, docs, source);
}

export function modifiersFromDocs(
  docs: readonly RawFirestoreDoc[],
  source: DocsSource = seedSource('modifiers'),
): DocumentWithId<Modifier>[] {
  return modelsFromDocs(Modifier, docs, source);
}

/**
 * Join the restaurant's modifier records into its modifier groups.
 *
 * A group stores only references (`modifierId` + contextual overrides); the
 * name and base price a cart line carries live on the modifier record. Each
 * group gets `resolvedModifiers`: the referenced records merged with the
 * reference's contextual overrides. MUTATES and returns `groups`.
 */
export function hydrateModifierGroups<G extends ModifierGroup>(
  groups: G[],
  globalModifiers: readonly Modifier[],
): G[] {
  groups.forEach((group) => {
    if (group.modifiers && group.modifiers.length > 0) {
      // Deduplicate references by modifierId before hydrating (prevents
      // duplicate modifiers from showing when the same ID appears in both
      // legacy 'modifiers' and 'modifierReferences' arrays pre-migration)
      const seenIds = new Set<number>();
      const uniqueRefs = group.modifiers.filter((ref) => {
        if (seenIds.has(ref.modifierId)) return false;
        seenIds.add(ref.modifierId);
        return true;
      });

      const hydrationList = uniqueRefs
        .map((ref) => {
          const globalMod = globalModifiers.find(
            (gm: any) => (gm.modifierId ?? gm.id) === ref.modifierId,
          );
          if (!globalMod) return null;

          // Merge logic: Global Mod properties + contextual overrides.
          // `as any` because the block below mutates modData with contextual
          // overrides (price/priceOverride/isDefault/position). This matched
          // the prior behavior when globalModifiers was implicitly any[] (via
          // the removed .catch); narrowing the type surfaced it, so keep the
          // local cast rather than change the hydration merge semantics.
          const modData = globalMod.toJSON() as any;

          // Apply contextual overrides from the reference
          if (ref.priceOverride !== undefined) modData.price = ref.priceOverride;
          // Preserve raw priceOverride for pricing resolution disambiguation
          modData.priceOverride = ref.priceOverride;
          if (ref.isDefault !== undefined) modData.isDefault = ref.isDefault;
          if (ref.position !== undefined) modData.position = ref.position;

          return new Modifier(modData);
        })
        .filter(Boolean) as Modifier[];

      // Populate resolvedModifiers with hydrated full Modifier objects
      (group as any).resolvedModifiers = hydrationList;
    }
  });
  return groups;
}

export function modifierGroupsFromDocs(
  groupDocs: readonly RawFirestoreDoc[],
  modifierDocs: readonly RawFirestoreDoc[],
): DocumentWithId<ModifierGroup>[] {
  return hydrateModifierGroups(
    modelsFromDocs(ModifierGroup, groupDocs, seedSource('modifier_groups')),
    modifiersFromDocs(modifierDocs),
  );
}

/**
 * The LIVE root menu groups: no parent, not archived, not explicitly inactive
 * (`isActive !== false` keeps legacy docs without the field visible).
 * What `useParentMenuGroups` serves and menu auto-selection runs over.
 */
export function selectParentMenuGroups<G extends MenuGroup>(groups: readonly G[]): G[] {
  return groups.filter(
    (group) =>
      (!group.parentIds || group.parentIds.length === 0) &&
      !group.isArchived &&
      group.isActive !== false,
  );
}

/** Every query the ordering menu reads, built from one server read. */
export interface MenuSeed {
  updatedAt: number;
  menuGroups: DocumentWithId<MenuGroup>[];
  parentMenuGroups: DocumentWithId<MenuGroup>[];
  items: DocumentWithId<Item>[];
  modifierGroups: DocumentWithId<ModifierGroup>[];
}

export function menuSeedFromDocs(docs: InitialMenuDocs): MenuSeed {
  const menuGroups = menuGroupsFromDocs(docs.menuGroups);
  return {
    updatedAt: docs.loadedAt,
    menuGroups,
    parentMenuGroups: selectParentMenuGroups(menuGroups),
    items: itemsFromDocs(docs.items),
    modifierGroups: modifierGroupsFromDocs(docs.modifierGroups, docs.modifiers),
  };
}
